/** One short question at a time, with the actual product always visible. */
import { useRef, useState } from 'react'
import {
  knowledgeCandidateClues,
  storeKnowledgeWriteSchema,
  type KnowledgeCatalogItem,
  type KnowledgeEntry,
  type KnowledgeResponse,
  type StoreKnowledgeWrite,
} from '@testfeed/integration-contracts'
import { storeKnowledgeCopy as copy } from '@testfeed/product-language'
import type { ShopifyTokenSource } from '../../shopifyApiFetch'
import { KnowledgeProduct } from './KnowledgeProduct'
import { KnowledgeTargetQuestion } from './KnowledgeTargetQuestion'
import { shopifyUiTracker, trackShopifyAction } from '../../shopifyUiTracking'
type Props = {
  shopify: ShopifyTokenSource
  item: KnowledgeCatalogItem
  origin?: 'suggested' | 'manual'
  entry?: KnowledgeEntry
  busy: boolean
  error: string | null
  suggestedTarget?: KnowledgeCatalogItem
  onSubmit: (input: StoreKnowledgeWrite) => Promise<void>
}
export function KnowledgeAnswerForm({ shopify, item, origin, entry, busy, error, suggestedTarget, onSubmit }: Props) {
  const [sample, setSample] = useState(false)
  const [invalid, setInvalid] = useState(false)
  const pending = useRef<{ payload: string; mutationId: string } | null>(null)
  async function submit(response: KnowledgeResponse) {
    const candidate = {
      origin,
      interaction: 'sample_check',
      questionId: entry?.questionId ?? null,
      expectedVersion: entry?.version ?? 0,
      subject: item.subject,
      scope: {
        kind: 'unresolved',
        description: 'Catalog item identity confirmed; sample use in orders has not been specified.',
      },
      response,
    }
    const payload = JSON.stringify(candidate)
    if (pending.current?.payload !== payload) pending.current = { payload, mutationId: crypto.randomUUID() }
    const parsed = storeKnowledgeWriteSchema.safeParse({
      ...candidate,
      mutationId: pending.current.mutationId,
    })
    setInvalid(!parsed.success)
    if (parsed.success) await onSubmit(parsed.data)
    else shopifyUiTracker(shopify).blocked('save-knowledge', 'validation')
  }
  const confirmSample = () =>
    submit({
      kind: 'answer',
      role: 'sample',
      targetState: 'unknown',
      targets: [],
      note: '',
    })
  const chooseTarget = (target: KnowledgeCatalogItem) =>
    submit({
      kind: 'answer',
      role: 'sample',
      targetState: 'selected',
      targets: [target.subject],
      note: '',
    })
  let reason = null
  if (item.freeOrders > 0) reason = <s-text color="subdued">{copy.freeReason}</s-text>
  else if (knowledgeCandidateClues(item).length) reason = <s-text color="subdued">{copy.catalogReason}</s-text>
  let question = (
    <s-section heading={copy.sampleQuestion}>
      <s-stack gap="base">
        <KnowledgeProduct item={item}>
          <s-stack gap="small-200">
            {reason}
            <s-text color="subdued">{copy.questionReason}</s-text>
          </s-stack>
        </KnowledgeProduct>
        <s-stack direction="inline" gap="small">
          <s-button
            variant="primary"
            disabled={busy}
            onClick={() => trackShopifyAction(shopify, 'answer-knowledge', () => setSample(true))}
          >
            {copy.yes}
          </s-button>
          <s-button disabled={busy} onClick={() => void submit({ kind: 'not_sample', note: '' })}>
            {copy.no}
          </s-button>
          <s-button disabled={busy} onClick={() => void submit({ kind: 'not_sure', note: '' })}>
            {copy.shortNotSure}
          </s-button>
        </s-stack>
      </s-stack>
    </s-section>
  )
  if (sample) {
    question = (
      <KnowledgeTargetQuestion
        shopify={shopify}
        item={item}
        suggestedTarget={suggestedTarget}
        busy={busy}
        onChoose={(target) => void chooseTarget(target)}
        onUnknown={() => void confirmSample()}
        onBack={() => trackShopifyAction(shopify, 'back-answer', () => setSample(false))}
      />
    )
  }
  let notice = null
  if (invalid) notice = <s-banner tone="warning">{copy.saveFailed}</s-banner>
  if (error) notice = <s-banner tone="critical">{error}</s-banner>
  return (
    <s-stack gap="base">
      {question}
      {notice}
    </s-stack>
  )
}
